import { useState, useMemo } from "react";
import {Percent, CreditCard, Banknote, Plane, Clock, Gift, ArrowRightLeft, DollarSign, } from 'lucide-react';
import { NumberInput } from "./Inputs";
import {
  calcInterest, calcCashAdvance, calcBalanceTransfer, calcForeignTx,
  calcAnnualFee, calcLateFee, calcPercentageFee, calcFinanceCharge,
  currency, num, safe,
} from "@/lib/calc";

type TabKey =
  | "interest" | "cashAdvance" | "balanceTransfer" | "foreignTx"
  | "exchange" | "annualFee" | "lateFee" | "percentage";

const TABS: { key: TabKey; label: string; icon: any }[] = [
  { key: "interest", label: "Interest", icon: Percent },
  { key: "cashAdvance", label: "Cash Advance", icon: Banknote },
  { key: "balanceTransfer", label: "Balance Transfer", icon: ArrowRightLeft },
  { key: "foreignTx", label: "Foreign TX", icon: Plane },
  { key: "exchange", label: "Exchange Rate", icon: DollarSign },
  { key: "annualFee", label: "Annual Fee", icon: Gift },
  { key: "lateFee", label: "Late Fee", icon: Clock },
  { key: "percentage", label: "Surcharge", icon: CreditCard },
];

function UniversalCalculator() {
  const [tab, setTab] = useState<TabKey>("interest");

  // Individual state per tab
  const [interest, setInterest] = useState({ balance: "2500", apr: "22.99", days: "30", payment: "" });
  const [cashAdv, setCashAdv] = useState({ amount: "500", feePct: "5", minFee: "10", apr: "29.99", days: "30" });
  const [bt, setBt] = useState({ amount: "5000", feePct: "3", introApr: "0", introMonths: "12", regularApr: "22.99", monthlyPayment: "250" });
  const [ft, setFt] = useState({ foreignAmount: "100", rate: "1.08", feePct: "3", markup: "0" });
  const [ex, setEx] = useState({ amount: "1000", rate: "1.08", feePct: "0" });
  const [af, setAf] = useState({ annualFee: "95", yearlySpend: "18000", rewardsPct: "1.5", credits: "120", perks: "0", signupBonus: "200" });
  const [lf, setLf] = useState({ missedAmount: "1200", lateFee: "40", apr: "24.99", daysLate: "10" });
  const [pct, setPct] = useState({ amount: "100", feePct: "3" });

  const interestRes = useMemo(
    () => calcInterest({ balance: safe(interest.balance), apr: safe(interest.apr), days: safe(interest.days), payment: safe(interest.payment) }),
    [interest]
  );
  const cashRes = useMemo(
    () => calcCashAdvance({ amount: safe(cashAdv.amount), feePct: safe(cashAdv.feePct), minFee: safe(cashAdv.minFee), apr: safe(cashAdv.apr), days: safe(cashAdv.days) }),
    [cashAdv]
  );
  const btRes = useMemo(
    () => calcBalanceTransfer({ amount: safe(bt.amount), feePct: safe(bt.feePct), introApr: safe(bt.introApr), introMonths: safe(bt.introMonths), regularApr: safe(bt.regularApr), monthlyPayment: safe(bt.monthlyPayment) }),
    [bt]
  );
  const ftRes = useMemo(
    () => calcForeignTx({ foreignAmount: safe(ft.foreignAmount), exchangeRate: safe(ft.rate), feePct: safe(ft.feePct), markupPct: safe(ft.markup) }),
    [ft]
  );
  const exRes = useMemo(
    () => calcForeignTx({ foreignAmount: safe(ex.amount), exchangeRate: safe(ex.rate), feePct: safe(ex.feePct) }),
    [ex]
  );
  const afRes = useMemo(
    () => calcAnnualFee({ annualFee: safe(af.annualFee), yearlySpend: safe(af.yearlySpend), rewardsPct: safe(af.rewardsPct), credits: safe(af.credits), perks: safe(af.perks), signupBonus: safe(af.signupBonus) }),
    [af]
  );
  const lfRes = useMemo(
    () => calcLateFee({ missedAmount: safe(lf.missedAmount), lateFee: safe(lf.lateFee), apr: safe(lf.apr), daysLate: safe(lf.daysLate) }),
    [lf]
  );
  const pctRes = useMemo(
    () => calcPercentageFee({ amount: safe(pct.amount), feePct: safe(pct.feePct) }),
    [pct]
  );

  return (
    <div className="card-soft p-5 md:p-6">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="font-serif text-xl md:text-2xl font-semibold text-slate-900">Universal Credit Card Fee Calculator</h2>
        <span className="pill">Updates live</span>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-3 -mx-1 px-1 mb-5 scrollbar-thin">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`tab-btn flex items-center gap-1.5 ${tab === t.key ? "active" : ""}`}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "interest" && (
        <div className="grid md:grid-cols-5 gap-5">
          <div className="md:col-span-3 grid sm:grid-cols-2 gap-4">
            <NumberInput label="Statement balance" prefix="$" value={interest.balance} onChange={(v) => setInterest({ ...interest, balance: v })} />
            <NumberInput label="APR" suffix="%" value={interest.apr} onChange={(v) => setInterest({ ...interest, apr: v })} />
            <NumberInput label="Billing cycle days" value={interest.days} onChange={(v) => setInterest({ ...interest, days: v })} />
            <NumberInput label="Payment (optional)" prefix="$" value={interest.payment} onChange={(v) => setInterest({ ...interest, payment: v })} helper="Reduces avg. daily balance." />
          </div>
          <ResultCard className="md:col-span-2">
            <ResultRow label="Daily periodic rate" value={`${num(interestRes.dailyPeriodicRate * 100, 5)}%`} />
            <ResultRow label="Avg. daily balance" value={currency(interestRes.averageDailyBalance)} />
            <TotalRow label="Estimated interest" value={currency(interestRes.interest)} />
            <ResultRow label="Balance + interest" value={currency(interestRes.total)} muted />
          </ResultCard>
        </div>
      )}

      {tab === "cashAdvance" && (
        <div className="grid md:grid-cols-5 gap-5">
          <div className="md:col-span-3 grid sm:grid-cols-2 gap-4">
            <NumberInput label="Cash advance amount" prefix="$" value={cashAdv.amount} onChange={(v) => setCashAdv({ ...cashAdv, amount: v })} />
            <NumberInput label="Fee percentage" suffix="%" value={cashAdv.feePct} onChange={(v) => setCashAdv({ ...cashAdv, feePct: v })} />
            <NumberInput label="Minimum flat fee" prefix="$" value={cashAdv.minFee} onChange={(v) => setCashAdv({ ...cashAdv, minFee: v })} />
            <NumberInput label="Cash advance APR" suffix="%" value={cashAdv.apr} onChange={(v) => setCashAdv({ ...cashAdv, apr: v })} />
            <NumberInput label="Days outstanding" value={cashAdv.days} onChange={(v) => setCashAdv({ ...cashAdv, days: v })} />
          </div>
          <ResultCard className="md:col-span-2">
            <ResultRow label="Advance fee" value={currency(cashRes.advanceFee)} />
            <ResultRow label="Interest" value={currency(cashRes.interest)} />
            <TotalRow label="Total cost" value={currency(cashRes.total)} />
            <ResultRow label="Amount + total cost" value={currency(cashRes.grandTotal)} muted />
          </ResultCard>
        </div>
      )}

      {tab === "balanceTransfer" && (
        <div className="grid md:grid-cols-5 gap-5">
          <div className="md:col-span-3 grid sm:grid-cols-2 gap-4">
            <NumberInput label="Transfer amount" prefix="$" value={bt.amount} onChange={(v) => setBt({ ...bt, amount: v })} />
            <NumberInput label="Transfer fee" suffix="%" value={bt.feePct} onChange={(v) => setBt({ ...bt, feePct: v })} />
            <NumberInput label="Intro APR" suffix="%" value={bt.introApr} onChange={(v) => setBt({ ...bt, introApr: v })} />
            <NumberInput label="Intro period" suffix="mo" value={bt.introMonths} onChange={(v) => setBt({ ...bt, introMonths: v })} />
            <NumberInput label="Regular APR" suffix="%" value={bt.regularApr} onChange={(v) => setBt({ ...bt, regularApr: v })} />
            <NumberInput label="Monthly payment" prefix="$" value={bt.monthlyPayment} onChange={(v) => setBt({ ...bt, monthlyPayment: v })} />
          </div>
          <ResultCard className="md:col-span-2">
            <ResultRow label="Transfer fee" value={currency(btRes.transferFee)} />
            <ResultRow label="Intro period interest" value={currency(btRes.introInterest)} />
            <ResultRow label="Post-intro interest" value={currency(btRes.postIntroInterest)} />
            <TotalRow label="Total cost (transfer)" value={currency(btRes.totalCostTransfer)} />
            <ResultRow
              label="Vs. keeping on original"
              value={`${btRes.savings >= 0 ? "Save" : "Lose"} ${currency(Math.abs(btRes.savings))}`}
              muted
            />
          </ResultCard>
        </div>
      )}

      {tab === "foreignTx" && (
        <div className="grid md:grid-cols-5 gap-5">
          <div className="md:col-span-3 grid sm:grid-cols-2 gap-4">
            <NumberInput label="Foreign amount" value={ft.foreignAmount} onChange={(v) => setFt({ ...ft, foreignAmount: v })} />
            <NumberInput label="Exchange rate" step={0.0001} value={ft.rate} onChange={(v) => setFt({ ...ft, rate: v })} helper="Home currency per 1 foreign unit." />
            <NumberInput label="Foreign TX fee" suffix="%" value={ft.feePct} onChange={(v) => setFt({ ...ft, feePct: v })} />
            <NumberInput label="Network markup (optional)" suffix="%" value={ft.markup} onChange={(v) => setFt({ ...ft, markup: v })} />
          </div>
          <ResultCard className="md:col-span-2">
            <ResultRow label="Converted amount" value={currency(ftRes.converted)} />
            <ResultRow label="Foreign TX fee" value={currency(ftRes.fee)} />
            <ResultRow label="Network markup" value={currency(ftRes.markup)} />
            <TotalRow label="Total cost" value={currency(ftRes.total)} />
          </ResultCard>
        </div>
      )}

      {tab === "exchange" && (
        <div className="grid md:grid-cols-5 gap-5">
          <div className="md:col-span-3 grid sm:grid-cols-2 gap-4">
            <NumberInput label="Amount" value={ex.amount} onChange={(v) => setEx({ ...ex, amount: v })} />
            <NumberInput label="Exchange rate" step={0.0001} value={ex.rate} onChange={(v) => setEx({ ...ex, rate: v })} />
            <NumberInput label="Issuer FX fee (optional)" suffix="%" value={ex.feePct} onChange={(v) => setEx({ ...ex, feePct: v })} />
          </div>
          <ResultCard className="md:col-span-2">
            <ResultRow label="Converted value" value={currency(exRes.converted)} />
            <ResultRow label="FX fee" value={currency(exRes.fee)} />
            <TotalRow label="Estimated total" value={currency(exRes.total)} />
          </ResultCard>
        </div>
      )}

      {tab === "annualFee" && (
        <div className="grid md:grid-cols-5 gap-5">
          <div className="md:col-span-3 grid sm:grid-cols-2 gap-4">
            <NumberInput label="Annual fee" prefix="$" value={af.annualFee} onChange={(v) => setAf({ ...af, annualFee: v })} />
            <NumberInput label="Yearly spending" prefix="$" value={af.yearlySpend} onChange={(v) => setAf({ ...af, yearlySpend: v })} />
            <NumberInput label="Rewards rate" suffix="%" value={af.rewardsPct} onChange={(v) => setAf({ ...af, rewardsPct: v })} />
            <NumberInput label="Statement credits" prefix="$" value={af.credits} onChange={(v) => setAf({ ...af, credits: v })} />
            <NumberInput label="Perk value (optional)" prefix="$" value={af.perks} onChange={(v) => setAf({ ...af, perks: v })} />
            <NumberInput label="Signup bonus (optional)" prefix="$" value={af.signupBonus} onChange={(v) => setAf({ ...af, signupBonus: v })} />
          </div>
          <ResultCard className="md:col-span-2">
            <ResultRow label="Annual rewards value" value={currency(afRes.rewards)} />
            <ResultRow label="Gross value" value={currency(afRes.gross)} />
            <TotalRow label="Net card value" value={currency(afRes.net)} positive={afRes.net >= 0} />
            <ResultRow label="Break-even spend" value={isFinite(afRes.breakEvenSpend) ? currency(afRes.breakEvenSpend) : "—"} muted />
          </ResultCard>
        </div>
      )}

      {tab === "lateFee" && (
        <div className="grid md:grid-cols-5 gap-5">
          <div className="md:col-span-3 grid sm:grid-cols-2 gap-4">
            <NumberInput label="Missed payment amount" prefix="$" value={lf.missedAmount} onChange={(v) => setLf({ ...lf, missedAmount: v })} />
            <NumberInput label="Late fee" prefix="$" value={lf.lateFee} onChange={(v) => setLf({ ...lf, lateFee: v })} />
            <NumberInput label="APR" suffix="%" value={lf.apr} onChange={(v) => setLf({ ...lf, apr: v })} />
            <NumberInput label="Days late" value={lf.daysLate} onChange={(v) => setLf({ ...lf, daysLate: v })} />
          </div>
          <ResultCard className="md:col-span-2">
            <ResultRow label="Late fee" value={currency(lfRes.lateFee)} />
            <ResultRow label="Late interest" value={currency(lfRes.interest)} />
            <TotalRow label="Estimated penalty" value={currency(lfRes.total)} />
          </ResultCard>
        </div>
      )}

      {tab === "percentage" && (
        <div className="grid md:grid-cols-5 gap-5">
          <div className="md:col-span-3 grid sm:grid-cols-2 gap-4">
            <NumberInput label="Transaction amount" prefix="$" value={pct.amount} onChange={(v) => setPct({ ...pct, amount: v })} />
            <NumberInput label="Fee percentage" suffix="%" value={pct.feePct} onChange={(v) => setPct({ ...pct, feePct: v })} />
          </div>
          <ResultCard className="md:col-span-2">
            <ResultRow label="Fee" value={currency(pctRes.fee)} />
            <TotalRow label="Total charged" value={currency(pctRes.total)} />
          </ResultCard>
        </div>
      )}

      <p className="text-xs text-slate-500 mt-5">
        These figures are estimates. Actual fees and interest depend on your card issuer's cardmember agreement.
      </p>

      <div className="sr-only">
        {/* Static calculated totals are also exposed here to help pre-render snapshot capture clean copy, no behavioral difference. */}
        <span>Finance charge example: {currency(calcFinanceCharge({ avgDailyBalance: 1800, apr: 21, days: 30 }).financeCharge)}</span>
      </div>
    </div>
  );
}

function ResultCard({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`rounded-2xl bg-slate-900 text-white p-5 flex flex-col gap-3 ${className}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Results</span>
        <span className="text-[10px] text-emerald-400 font-semibold">LIVE</span>
      </div>
      {children}
    </div>
  );
}

function ResultRow({ label, value, muted = false }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className={muted ? "text-slate-500" : "text-slate-300"}>{label}</span>
      <span className={`num font-semibold ${muted ? "text-slate-400" : "text-white"}`}>{value}</span>
    </div>
  );
}

function TotalRow({ label, value, positive = true }: { label: string; value: string; positive?: boolean }) {
  return (
    <div className={`mt-1 rounded-xl p-3 border ${positive ? "bg-emerald-500/10 border-emerald-500/30" : "bg-rose-500/10 border-rose-500/30"}`}>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-300 mb-0.5">{label}</div>
      <div className={`num text-2xl font-semibold ${positive ? "text-emerald-300" : "text-rose-300"}`}>{value}</div>
    </div>
  );
}

export default UniversalCalculator;
